import "moment/locale/en-sg";

import moment from "moment";

moment.updateLocale("en-sg", { week: { dow: 0 } });

import { NX_DATE_LOCALE } from "@allianz/ng-aquila/datefield";
import { NxModalModule } from "@allianz/ng-aquila/modal";
import { NxMomentDateModule } from "@allianz/ng-aquila/moment-date-adapter";
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from "@angular/common/http";
import { ApplicationConfig, importProvidersFrom } from "@angular/core";
import { provideRouter, withPreloading } from "@angular/router";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { NgxsReduxDevtoolsPluginModule } from "@ngxs/devtools-plugin";
import { NgxsLoggerPluginModule } from "@ngxs/logger-plugin";
import { NgxsStoragePluginModule } from "@ngxs/storage-plugin";
import { NgxsModule } from "@ngxs/store";
import { CredentialsInterceptorService } from "@services/credentials-interceptor/credentials-interceptor.service";
import { LoadingInterceptorService } from "@services/loading-interceptor/loading-interceptor.service";
import { RequestInterceptorService } from "@services/request-interceptor/request-interceptor.service";
import { AuthenticatedPreloadStrategy } from "@shared/router/authenticated-preload.strategy";
import { UserService } from "@shared/services/user/user.service";
import { AdministratorService } from "@store/administrator/administrator.service";
import { AdministratorState } from "@store/administrator/administrator.state";
import { CampaignService } from "@store/campaign/campaign.service";
import { CampaignState } from "@store/campaign/campaign.state";
import { CustomerManagementService } from "@store/customer-management/customer-management.service";
import { CustomerManagementState } from "@store/customer-management/customer-management.state";
import { DashboardService } from "@store/dashboard/dashboard.service";
import { DashboardState } from "@store/dashboard/dashboard.state";
import { ELibraryService } from "@store/e-library/e-library.service";
import { ELibraryState } from "@store/e-library/e-library.state";
import { EStatementService } from "@store/e-statement/e-statement.service";
import { EStatementState } from "@store/e-statement/e-statement.state";
import { ESubmissionService } from "@store/e-submission/e-submission.service";
import { ESubmissionState } from "@store/e-submission/e-submission.state";
import { EventService } from "@store/event-management/event.service";
import { EventState } from "@store/event-management/event.state";
import { ErrorService } from "@store/exception/error.service";
import { ErrorState } from "@store/exception/error.state";
import { LandingPageService } from "@store/landing-page/landing-page.service";
import { LandingPageState } from "@store/landing-page/landing-page.state";
import { LoginState } from "@store/login/login.state";
import { NotificationService } from "@store/notification/notification.service";
import { NotificationState } from "@store/notification/notification.state";
import { ProfileService } from "@store/profile/profile.service";
import { ProfileState } from "@store/profile/profile.state";
import { UserPerformanceService } from "@store/user-performance/user-performance.service";
import { UserPerformanceState } from "@store/user-performance/user-performance.state";

import { environment as env } from "../environments/environment";
import { routes } from "./app.routes";
import { CIService } from "./store/ci/certificate-of-insurance.service";
import { CIState } from "./store/ci/certificate-of-insurance.state";
import { CIReportService } from "./store/ci-report/ci-report.service";
import { CIReportState } from "./store/ci-report/ci-report.state";
import { EScheduleService } from "./store/new-e-schedule/new-e-schedule.service";
import { SalesManagementReportsService } from "./store/sales-management-reports/sales-management-reports.service";
import { SalesManagementReportsState } from "./store/sales-management-reports/sales-management-reports.state";
import { UserState } from "./store/user/user.state";

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, "./assets/i18n/", ".json?4");
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes, withPreloading(AuthenticatedPreloadStrategy)),
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CredentialsInterceptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RequestInterceptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoadingInterceptorService,
      multi: true,
    },
    importProvidersFrom(
      NgxsModule.forRoot(
        [
          UserState,
          DashboardState,
          LandingPageState,
          LoginState,
          ProfileState,
          SalesManagementReportsState,
          ELibraryState,
          CustomerManagementState,
          ESubmissionState,
          CampaignState,
          EventState,
          ErrorState,
          UserPerformanceState,
          CIState,
          CIReportState,
          AdministratorState,
          EStatementState,
          NotificationState,
        ],
        {
          developmentMode: !env.production,
        },
      ),
      NgxsStoragePluginModule.forRoot({
        keys: [
          "ELibraryState.filterLOV",
          "CustomerManagementState.folderContents",
        ],
      }),
      NgxsReduxDevtoolsPluginModule.forRoot(),
      NgxsLoggerPluginModule.forRoot({
        disabled: env.production, // Disable logging in production
      }),
    ),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient],
        },
      }),
    ),
    { provide: NX_DATE_LOCALE, useValue: "en-sg" },
    UserService,
    DashboardService,
    LandingPageService,
    ESubmissionService,
    NotificationService,
    ProfileService,
    CIService,
    EScheduleService,
    SalesManagementReportsService,
    ELibraryService,
    CustomerManagementService,
    CIService,
    CIReportService,
    EScheduleService,
    importProvidersFrom(NxModalModule.forRoot(), NxMomentDateModule),
    CampaignService,
    EventService,
    ErrorService,
    UserPerformanceService,
    EStatementService,
    ProfileService,
    AdministratorService,
  ],
};
